import type { CustomChartGrammar, JsonObject } from "./types.js";
import { VEGA_DATASET_NAME } from "./types.js";
import { isObject } from "./json.js";

// Keys the host owns, not the spec: the dataset is injected at render time,
// and sizing and theme come from the Report grid cell. Stripping them (rather
// than rejecting) keeps a pasted example from the Vega/Vega-Lite docs working
// as-is, and keeps inline data from ever shadowing the bound dataset.
const HOST_KEYS: Record<CustomChartGrammar, readonly string[]> = {
  "vega-lite": [
    "$schema",
    "data",
    "width",
    "height",
    "autosize",
    "config",
    "background",
    "padding",
  ],
  vega: [
    "$schema",
    "width",
    "height",
    "autosize",
    "config",
    "background",
    "padding",
  ],
};

/** Drops inline `values` from a Vega spec's `table` entry — the dataset always wins. */
function withoutTableValues(data: unknown): unknown {
  if (!Array.isArray(data)) return data;
  return data.map((entry) => {
    if (!isObject(entry) || entry.name !== VEGA_DATASET_NAME) return entry;
    const { values: _values, ...rest } = entry;
    return rest;
  });
}

/** Returns a copy of `spec` without the keys the host controls for `grammar`. */
export function stripHostControlledKeys(
  grammar: CustomChartGrammar,
  spec: JsonObject,
): JsonObject {
  const cleaned: JsonObject = { ...spec };
  for (const key of HOST_KEYS[grammar]) delete cleaned[key];
  if (grammar === "vega" && "data" in cleaned) {
    cleaned.data = withoutTableValues(cleaned.data);
  }
  return cleaned;
}
